import { fetchStrapiSingleType } from './strapiClient';
import { pickCmsText, pickCmsTitle } from '../utils/cmsContent';
import {
  DEFAULT_CTA_BODY,
  DEFAULT_CTA_TITLE,
  DEFAULT_FEATURE_COLUMNS,
  DEFAULT_FORFAITS,
  DEFAULT_SECTION_FORFAITS_BEFORE,
  DEFAULT_SECTION_FORFAITS_HIGHLIGHT,
  DEFAULT_SECTION_SERVICES_BEFORE,
  DEFAULT_SECTION_SERVICES_HIGHLIGHT,
  DEFAULT_SECTION_TESTIMONIALS_BEFORE,
  DEFAULT_SECTION_TESTIMONIALS_HIGHLIGHT,
  DEFAULT_SERVICE_CARDS,
  DEFAULT_SUBTITLE,
  DEFAULT_TESTIMONIALS,
} from '../content/expertConnectionDefaults';

/** Populate imbriqué (composants + lignes). */
export const EXPERT_CONNECTION_POPULATE =
  'populate[featureColumns]=*&populate[forfaits][populate][featureLines]=*&populate[serviceCards][populate][featureLines]=*&populate[testimonials]=*';

function unwrap(entry) {
  if (entry == null || typeof entry !== 'object') return {};
  if (entry.attributes && typeof entry.attributes === 'object' && !Array.isArray(entry.attributes)) {
    return { id: entry.id, ...entry.attributes };
  }
  return entry;
}

function normalizeFeatureLines(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      const o = unwrap(e);
      const t = o.text != null ? String(o.text).trim() : '';
      return t;
    })
    .filter(Boolean);
}

function normalizeFeatureColumns(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      const o = unwrap(e);
      const title = o.title != null ? String(o.title).trim() : '';
      const body = o.body != null ? String(o.body).trim() : '';
      return { title, body };
    })
    .filter((x) => x.title || x.body);
}

function normalizeForfaits(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      const o = unwrap(e);
      const tabLabel = o.tabLabel != null ? String(o.tabLabel).trim() : '';
      const cardTitle = o.cardTitle != null ? String(o.cardTitle).trim() : '';
      const cardTagline = o.cardTagline != null ? String(o.cardTagline).trim() : '';
      const priceAmount = o.priceAmount != null ? String(o.priceAmount).trim() : '';
      const priceSuffix = o.priceSuffix != null ? String(o.priceSuffix).trim() : 'TTC';
      const installment = o.installment != null ? String(o.installment).trim() : '';
      const featureLines = normalizeFeatureLines(o.featureLines);
      const contractPath = o.contractPath != null ? String(o.contractPath).trim() : '';
      const buttonLabel = o.buttonLabel != null ? String(o.buttonLabel).trim() : '';
      return {
        tabLabel,
        cardTitle,
        cardTagline,
        priceAmount,
        priceSuffix,
        installment,
        featureLines,
        contractPath,
        buttonLabel,
      };
    })
    .filter(
      (x) =>
        x.cardTitle ||
        x.priceAmount ||
        x.featureLines.length > 0 ||
        x.contractPath ||
        x.cardTagline ||
        x.installment
    );
}

function normalizeServiceCards(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      const o = unwrap(e);
      const titleLine1 = o.titleLine1 != null ? String(o.titleLine1).trim() : '';
      const titleLine2 = o.titleLine2 != null ? String(o.titleLine2).trim() : '';
      const priceAmount = o.priceAmount != null ? String(o.priceAmount).trim() : '';
      const priceSuffix = o.priceSuffix != null ? String(o.priceSuffix).trim() : 'TTC';
      const installment = o.installment != null ? String(o.installment).trim() : '';
      const featureLines = normalizeFeatureLines(o.featureLines);
      const contractPath = o.contractPath != null ? String(o.contractPath).trim() : '';
      const buttonLabel = o.buttonLabel != null ? String(o.buttonLabel).trim() : '';
      return {
        titleLine1,
        titleLine2,
        priceAmount,
        priceSuffix,
        installment,
        featureLines,
        contractPath,
        buttonLabel,
      };
    })
    .filter(
      (x) =>
        x.titleLine1 ||
        x.priceAmount ||
        x.featureLines.length > 0 ||
        x.contractPath ||
        x.installment
    );
}

function normalizeTestimonials(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      const o = unwrap(e);
      const quote = o.quote != null ? String(o.quote).trim() : '';
      const attribution = o.attribution != null ? String(o.attribution).trim() : '';
      return { quote, attribution };
    })
    .filter((x) => x.quote || x.attribution);
}

/**
 * @param {object|null} attrs attributs Strapi (après json.data.attributes)
 * @returns {object} modèle fusionné prêt pour l’UI
 */
export function buildExpertConnectionPageModel(attrs) {
  const featureColumnsNorm = normalizeFeatureColumns(attrs?.featureColumns);
  const forfaitsNorm = normalizeForfaits(attrs?.forfaits);
  const serviceCardsNorm = normalizeServiceCards(attrs?.serviceCards);
  const testimonialsNorm = normalizeTestimonials(attrs?.testimonials);

  const featureColumns = featureColumnsNorm.length > 0 ? featureColumnsNorm : DEFAULT_FEATURE_COLUMNS;
  const forfaits = forfaitsNorm.length > 0 ? forfaitsNorm : DEFAULT_FORFAITS;
  const serviceCards = serviceCardsNorm.length > 0 ? serviceCardsNorm : DEFAULT_SERVICE_CARDS;
  const testimonials = testimonialsNorm.length > 0 ? testimonialsNorm : DEFAULT_TESTIMONIALS;

  return {
    title: attrs?.title,
    subtitle: attrs?.subtitle,
    featureColumns,
    forfaitsSectionTitleBefore: pickCmsText(attrs?.forfaitsSectionTitleBefore, DEFAULT_SECTION_FORFAITS_BEFORE),
    forfaitsSectionTitleHighlight: pickCmsText(
      attrs?.forfaitsSectionTitleHighlight,
      DEFAULT_SECTION_FORFAITS_HIGHLIGHT
    ),
    forfaits,
    servicesSectionTitleBefore: pickCmsText(attrs?.servicesSectionTitleBefore, DEFAULT_SECTION_SERVICES_BEFORE),
    servicesSectionTitleHighlight: pickCmsText(
      attrs?.servicesSectionTitleHighlight,
      DEFAULT_SECTION_SERVICES_HIGHLIGHT
    ),
    serviceCards,
    ctaTitle: pickCmsText(attrs?.ctaTitle, DEFAULT_CTA_TITLE),
    ctaBody: pickCmsText(attrs?.ctaBody, DEFAULT_CTA_BODY),
    testimonialsSectionTitleBefore: pickCmsText(
      attrs?.testimonialsSectionTitleBefore,
      DEFAULT_SECTION_TESTIMONIALS_BEFORE
    ),
    testimonialsSectionTitleHighlight: pickCmsText(
      attrs?.testimonialsSectionTitleHighlight,
      DEFAULT_SECTION_TESTIMONIALS_HIGHLIGHT
    ),
    testimonials,
  };
}

export async function fetchExpertConnectionPage() {
  try {
    const attrs = await fetchStrapiSingleType('page-expert-connection', {
      populate: EXPERT_CONNECTION_POPULATE,
    });
    const model = buildExpertConnectionPageModel(attrs);
    return {
      ...model,
      pageTitle: pickCmsTitle(attrs?.title, 'Expert Connection'),
      pageSubtitle: pickCmsText(attrs?.subtitle, DEFAULT_SUBTITLE),
    };
  } catch {
    const model = buildExpertConnectionPageModel(null);
    return {
      ...model,
      pageTitle: 'Expert Connection',
      pageSubtitle: DEFAULT_SUBTITLE,
    };
  }
}
